import type { ReactNode } from 'react';
import { MessageHubSearchField } from './MessageHubSearchField';
import { MessageHubLoading } from './MessageHubLoading';
import { MessageHubListPagination } from './MessageHubListPagination';

const AVATAR_COLORS = [
  'mh-avatar--c1',
  'mh-avatar--c2',
  'mh-avatar--c3',
  'mh-avatar--c4',
  'mh-avatar--c5',
  'mh-avatar--c6',
  'mh-avatar--c7',
  'mh-avatar--c8',
];

export function hubAvatarClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function hubInitials(name: string, max = 2): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, max)
    .toUpperCase();
}

export function formatHubTime(isoOrDisplay: string): string {
  if (!isoOrDisplay) return '';
  const date = new Date(isoOrDisplay);
  if (Number.isNaN(date.getTime())) return isoOrDisplay;
  const now = new Date();
  const diffH = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffH < 24) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffH < 48) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

type ListColumnProps = {
  title: string;
  searchPlaceholder: string;
  search: string;
  onSearchChange: (value: string) => void;
  loading: boolean;
  loadingLabel: string;
  emptyMessage: string;
  listPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  children: ReactNode;
};

export function MessageHubListColumn({
  title,
  searchPlaceholder,
  search,
  onSearchChange,
  loading,
  loadingLabel,
  emptyMessage,
  listPage,
  totalItems,
  onPageChange,
  children,
}: ListColumnProps) {
  return (
    <>
      <div className="mh-inbox-toolbar">
        <h3>{title}</h3>
        <MessageHubSearchField
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label={searchPlaceholder}
        />
      </div>
      {loading ? (
        <div className="mh-inbox-loading-slot">
          <MessageHubLoading label={loadingLabel} />
        </div>
      ) : (
        <>
          <div className="mh-inbox-list">
            {totalItems > 0 ? (
              children
            ) : (
              <div className="mh-list-empty">
                <p>{emptyMessage}</p>
              </div>
            )}
          </div>
          <MessageHubListPagination
            page={listPage}
            totalItems={totalItems}
            onPageChange={onPageChange}
          />
        </>
      )}
    </>
  );
}

type DetailPanelProps = {
  onBack?: () => void;
  title: string;
  subtitle?: string;
  avatar?: ReactNode;
  headerExtra?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
};

export function MessageHubDetailPanel({
  onBack,
  title,
  subtitle,
  avatar,
  headerExtra,
  children,
  footer,
}: DetailPanelProps) {
  return (
    <div className="mh-thread-panel">
      <div className="mh-thread-panel-inner">
        {onBack ? (
          <div className="mh-thread-header mh-thread-header--mobile-only">
            <button type="button" className="mh-mobile-back" onClick={onBack}>
              ← Back
            </button>
          </div>
        ) : null}
        <header className="mh-thread-header">
          {avatar}
          <div className="mh-thread-header-text">
            <p className="mh-thread-header-name">{title}</p>
            {subtitle ? <p className="mh-thread-header-sub">{subtitle}</p> : null}
          </div>
          {headerExtra}
        </header>
        <div className="mh-thread-messages mh-detail-scroll">{children}</div>
        {footer ? <div className="mh-detail-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

type EmptyDetailProps = {
  icon: ReactNode;
  title: string;
  description: string;
};

export function MessageHubEmptyDetail({ icon, title, description }: EmptyDetailProps) {
  return (
    <div className="mh-thread-panel mh-thread-panel--empty">
      <div className="mh-empty-state">
        <div className="mh-empty-state-icon">{icon}</div>
        <p className="text-sm font-medium text-slate-700">{title}</p>
        <p className="mt-1 max-w-xs text-xs text-slate-500">{description}</p>
      </div>
    </div>
  );
}

export function HubAvatar({
  name,
  large,
  online,
  imageUrl,
}: {
  name: string;
  large?: boolean;
  online?: boolean;
  imageUrl?: string | null;
}) {
  const initials = hubInitials(name) || '?';
  return (
    <div className={`mh-avatar-wrap ${large ? 'mh-avatar-wrap--thread' : ''}`}>
      <div
        className={`mh-avatar ${hubAvatarClass(name || 'U')} ${large ? 'mh-avatar--thread' : ''}`}
        aria-label={name}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="h-full w-full rounded-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          initials
        )}
      </div>
      {online ? <span className="mh-avatar-online" aria-label="Online" /> : null}
    </div>
  );
}

type ListItemProps = {
  active?: boolean;
  onClick: () => void;
  avatar: ReactNode;
  name: string;
  meta?: string;
  preview: string;
  time?: string;
  badges?: ReactNode;
};

export function MessageHubListItem({
  active,
  onClick,
  avatar,
  name,
  meta,
  preview,
  time,
  badges,
}: ListItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mh-conversation-item ${active ? 'mh-conversation-item--active' : ''}`}
    >
      {avatar}
      <div className="mh-conversation-meta">
        <p className="mh-conversation-name">{name}</p>
        {meta ? <p className="mh-conversation-sub">{meta}</p> : null}
        <p className="mh-conversation-preview">{preview}</p>
        {badges ? <div className="mh-conversation-badges">{badges}</div> : null}
      </div>
      {time ? <span className="mh-conversation-time">{time}</span> : null}
    </button>
  );
}

export function HubStatusBadge({
  status,
  variant = 'neutral',
}: {
  status: string;
  variant?: 'neutral' | 'unread' | 'success' | 'warning' | 'danger' | 'pending';
}) {
  return <span className={`mh-status-badge mh-status-badge--${variant}`}>{status}</span>;
}

export function HubSectionChip({ label }: { label: string }) {
  return <span className="mh-section-chip">{label}</span>;
}
